import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {FILTERS, MultiValueFilter} from 'search/SearchConstants';
import {
  CustomParty,
  EntityWithTextAndSuspended,
  MinimalEntity,
} from 'ui-library/entity-input/EntityInputConstants';
import {isCustomParty} from 'ui-library/entity-input/EntityInputUtil';
import {EntityInputWithSearch, UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import {capitalize, listToReadable} from 'util/NameUtil';
import pick from 'util/pick';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import FilterFormSkeletonNodeLayout from './base/FilterFormSkeletonNodeLayout';
import PrimaryFilterFormSkeleton from './base/PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './base/SecondaryFilterFormSkeleton';

interface FilterParty {
  id: string;
  handle?: string;
  name?: string;
}

function _getDisplayName(
  allTeams: Array<FilterParty>,
  allUsers: Array<FilterParty>,
  allOrganizations: Array<FilterParty>,
  id: string
): string {
  const team = allTeams.find((t) => t.id === id);
  if (team) {
    return getPartyDisplayString(team) ?? '';
  }
  const user = allUsers.find((u) => u.id === id);
  if (user) {
    return getPartyDisplayString(user) ?? '';
  }
  const orgs = allOrganizations.find((o) => o.id === id);
  if (orgs) {
    return getPartyDisplayString(orgs) ?? '';
  }
  return '';
}

export interface PartiesApiIdFilterFormProps extends BaseFilterFormProps {
  filter: MultiValueFilter; // values are IDs
  filterStyle: FilterStyle;
  onChangeFilter: (filter: MultiValueFilter) => void;
  onEnter?: () => void;
  onRemove?: () => void;
  customPartyOptions?: Array<string>;
}

interface ExtraProps {
  allowTeams: boolean;
  allowOrganizations: boolean;
  allOrganizations: Array<FilterParty>;
  allTeams: Array<FilterParty>;
  allUsers: Array<FilterParty>;
  userSearchType: UserSearchType;
}

export default function PartiesByApiIdFilterForm(
  props: PartiesApiIdFilterFormProps & ExtraProps
): JSX.Element {
  const skeletonProps = pick(props, ...BASE_FILTER_FORM_PROP_NAMES);
  assert(
    props.userSearchType !== UserSearchType.None || props.allowTeams || props.allowOrganizations,
    'Expected at least one allowed party type'
  );
  const entitiesReadable = listToReadable(
    _.compact([
      props.userSearchType !== UserSearchType.None && 'users',
      props.allowTeams && 'teams',
      props.allowOrganizations && 'organizations',
    ]),
    'or'
  );
  const filterKeyToDisplayName = (filterKey: string): string =>
    capitalize(filterKey.replace(/_/g, ' ').toLowerCase());

  const value: Array<MinimalEntity | CustomParty> = props.filter.value.map((id) => ({
    handle: '',
    id,
  }));

  const partyInput = (
    <EntityInputWithSearch
      allowAdjustInputWidth={false}
      size={TagsInputSize.SMALL}
      onChange={(entities: Array<EntityWithTextAndSuspended | CustomParty>) => {
        props.onChangeFilter({
          ...props.filter,
          value: entities
            .filter((entity) => !isCustomParty(entity))
            .map((entity: EntityWithTextAndSuspended) => entity.id),
        });
      }}
      onEnter={props.onEnter}
      userSearchType={props.userSearchType}
      allowTeams={props.allowTeams}
      allowOrganizations={props.allowOrganizations}
      organizationIds={props.allOrganizations.map(({id}) => id)}
      value={value}
      enableNewEntityInput={true}
      shouldAllowMultiple={true}
      emptyText={`No ${entitiesReadable} found`}
      customPartyOptions={props.customPartyOptions}
    />
  );

  if (props.filterStyle === FilterStyle.PRIMARY) {
    const filterName = FILTERS[props.filter.key].name ?? filterKeyToDisplayName(props.filter.key);
    return (
      <PrimaryFilterFormSkeleton
        {...skeletonProps}
        buttonSize={props.buttonSize}
        content={<FilterFormSkeletonNodeLayout nodeGrid={[[partyInput]]} />}
        dropdownToggleText={
          props.filter.value.length
            ? `${filterName}: ${props.filter.value
                .map((id) => _getDisplayName(props.allTeams, props.allUsers, props.allOrganizations, id))
                .join(', ')}`
            : filterName
        }
      />
    );
  } else {
    return (
      <SecondaryFilterFormSkeleton
        {...skeletonProps}
        nodeGrid={[[partyInput]]}
        onChangeFilterKey={props.onChangeFilterKey}
        selectedFilterKey={props.selectedFilterKey}
        onRemove={props.onRemove!}
      />
    );
  }
}
